import { db } from '../firebaseConfig';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { getTodayDateKey } from '../utils/dateHelpers';
import storage from '../services/storageService';
import * as dbService from '../services/dbService';
import { createContext, useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isAdmin } from '../config/adminConfig';

// Criar o contexto
const AppContext = createContext(null);

// Categorias padrão
const DEFAULT_INCOME_CATEGORIES = ['Uber', '99', 'iFood', 'Rappi', 'Zé Delivery', 'Lalamove', 'Loggi', 'inDrive', 'Particular', 'Gorjeta'];
const DEFAULT_EXPENSE_CATEGORIES = ['Gasolina', 'Etanol', 'GNV', 'Elétrico', 'Alimentação', 'Manutenção', 'Lava Jato', 'Contas Fixas'];

const defaultDynamicGoalsConfig = {
    enabled: false,
    weights: { 0: 1.2, 1: 1.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 1.5, 6: 1.5 } // 0 = Domingo, 1 = Segunda, etc.
};

const defaultMaintenanceFundConfig = {
    enabled: false,
    type: 'percentage', // ou 'fixed_daily' ou 'fixed_km'
    value: 10 // 10% ou R$10
};

export const DEFAULT_THEME_SCHEDULE = { darkStart: '18:00', darkEnd: '06:00' };

/**
 * Verifica se o horário atual está dentro da faixa definida para o modo escuro
 */
export const isTimeInDarkSchedule = (schedule) => {
    try {
        const darkStart = schedule?.darkStart || DEFAULT_THEME_SCHEDULE.darkStart;
        const darkEnd = schedule?.darkEnd || DEFAULT_THEME_SCHEDULE.darkEnd;
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const [startH, startM] = darkStart.split(':').map(Number);
        const [endH, endM] = darkEnd.split(':').map(Number);
        const startMinutes = (Number.isFinite(startH) ? startH : 18) * 60 + (Number.isFinite(startM) ? startM : 0);
        const endMinutes = (Number.isFinite(endH) ? endH : 6) * 60 + (Number.isFinite(endM) ? endM : 0);

        if (startMinutes === endMinutes) return false;

        if (startMinutes < endMinutes) {
            return currentMinutes >= startMinutes && currentMinutes < endMinutes;
        } else {
            return currentMinutes >= startMinutes || currentMinutes < endMinutes;
        }
    } catch (e) {
        return false;
    }
};

/**
 * Provider do contexto da aplicação
 * Gerencia estado global compartilhado entre componentes
 */
export function AppProvider({ children, firebaseUser }) {
    // --- ESTADOS PRINCIPAIS ---
    const [transactions, setTransactions] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [userProfile, setUserProfile] = useState({ name: 'Piloto', photo: null });
    const [theme, setTheme] = useState(() => storage.getItemSync('rotaDoLucroTheme', 'system'));
    const [themeSchedule, setThemeSchedule] = useState(() => {
        const saved = storage.getItemSync('rotaDoLucroThemeSchedule', null);
        return saved ? { darkStart: saved.darkStart || '18:00', darkEnd: saved.darkEnd || '06:00' } : DEFAULT_THEME_SCHEDULE;
    });
    const [timeTick, setTimeTick] = useState(() => Date.now());

    // Sincroniza periodicamente com o relógio para alternar o tema automático por horário
    useEffect(() => {
        if (theme !== 'schedule') return;
        const interval = setInterval(() => {
            setTimeTick(Date.now());
        }, 30000);
        return () => clearInterval(interval);
    }, [theme]);

    const updateThemeSchedule = useCallback((newSchedule) => {
        setThemeSchedule(prev => {
            const updated = { ...prev, ...newSchedule };
            if (typeof storage.setItemSync === 'function') {
                storage.setItemSync('rotaDoLucroThemeSchedule', updated);
            } else if (typeof storage.setItem === 'function') {
                storage.setItem('rotaDoLucroThemeSchedule', updated);
            }
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    window.localStorage.setItem('rotaDoLucroThemeSchedule', JSON.stringify(updated));
                }
            } catch (e) {}
            return updated;
        });
    }, []);

    const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
    const [kmRecords, setKmRecords] = useState({});
    const [customCategories, setCustomCategories] = useState({ income: [], expense: [] });
    const [fixedExpenses, setFixedExpenses] = useState([]);
    const [scheduledTrips, setScheduledTrips] = useState([]);
    const [dailyManualStats, setDailyManualStats] = useState({});
    const [deletedDefaultCategories, setDeletedDefaultCategories] = useState(() => storage.getItemSync('rotaDoLucroDeletedCategories', { income: [], expense: [] }));
    
    // Logo personalizada do aplicativo (armazenada exclusivamente neste dispositivo)
    const [customAppLogo, setCustomAppLogo] = useState(() => storage.getItemSync('custom_app_header_logo', null));

    const updateCustomLogo = useCallback((logoDataUrl) => {
        if (logoDataUrl) {
            if (typeof storage.setItemSync === 'function') {
                storage.setItemSync('custom_app_header_logo', logoDataUrl);
            } else if (typeof storage.setItem === 'function') {
                storage.setItem('custom_app_header_logo', logoDataUrl);
            }
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    window.localStorage.setItem('custom_app_header_logo', JSON.stringify(logoDataUrl));
                }
            } catch (e) {}
            setCustomAppLogo(logoDataUrl);
        } else {
            if (typeof storage.removeItemSync === 'function') {
                storage.removeItemSync('custom_app_header_logo');
            } else if (typeof storage.removeItem === 'function') {
                storage.removeItem('custom_app_header_logo');
            }
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    window.localStorage.removeItem('custom_app_header_logo');
                }
            } catch (e) {}
            setCustomAppLogo(null);
        }
    }, []);

    // --- ESTADOS DE ASSINATURA ---
    // Opcao D: Cache-first com validacao de expiresAt.
    // Usa cache local imediatamente se a assinatura ainda esta valida (data futura).
    // Firestore confirma/atualiza em background. Seguro e sem flash visual.
    const resolveSubscriptionFromCache = () => {
        const cached = storage.getItemSync('rotaDoLucroSubscription', null);
        if (!cached) return { plan: 'free', expiresAt: null };
        const raw = cached.expiresAt;
        let expiresDate = null;
        if (raw) {
            if (raw.toDate) expiresDate = raw.toDate();
            else expiresDate = new Date(raw);
        }
        const isStillValid = expiresDate && !isNaN(expiresDate.getTime()) && expiresDate.getTime() > Date.now();
        const isVip = cached.plan === 'vip';
        const isTrialActive = cached.isTrial === true && isStillValid;
        if (isVip || isStillValid || isTrialActive) {
            console.log('[APP_CONTEXT] Cache-first: assinatura valida em cache:', cached.plan);
            return cached;
        }
        return { plan: 'free', expiresAt: null };
    };
    const [subscription, setSubscription] = useState(resolveSubscriptionFromCache);
    const [isSubscriptionLoaded, setIsSubscriptionLoaded] = useState(() => {
        const cached = storage.getItemSync('rotaDoLucroSubscription', null);
        if (!cached) return false;
        const raw = cached.expiresAt;
        let expiresDate = null;
        if (raw) {
            if (raw.toDate) expiresDate = raw.toDate();
            else expiresDate = new Date(raw);
        }
        const isStillValid = expiresDate && !isNaN(expiresDate.getTime()) && expiresDate.getTime() > Date.now();
        return cached.plan === 'vip' || isStillValid || cached.isTrial === true;
    });

    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    const [isStorageReady, setIsStorageReady] = useState(false); // NOVO: Flag de cache pronto
    const [isLocalDataLoaded, setIsLocalDataLoaded] = useState(false); // Flag para carregar do storage nativo
    const [lastRemoteSync, setLastRemoteSync] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastResetMonth, setLastResetMonth] = useState(''); // Tracking para reset mensal
    const [lastRolloverMonth, setLastRolloverMonth] = useState(() => storage.getItemSync('meupainel-last-rollover-month', ''));

    // --- NOVOS ESTADOS REPLICADOS DO APP.JS ---
    const [pastDueExpenses, setPastDueExpenses] = useState([]);
    const [manualVarCost, setManualVarCost] = useState(0);
    const [manualSavedAmount, setManualSavedAmount] = useState(0);
    const [manualVarCostType, setManualVarCostType] = useState('monthly');
    const [varCostMode, setVarCostMode] = useState('auto');
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);

    // --- NOVOS ESTADOS: Metas Dinâmicas e Fundo de Manutenção ---
    const [dynamicGoalsConfig, setDynamicGoalsConfig] = useState(() => storage.getItemSync('rotaDoLucroDynamicGoalsConfig', defaultDynamicGoalsConfig));
    const [maintenanceFundConfig, setMaintenanceFundConfig] = useState(() => storage.getItemSync('rotaDoLucroMaintenanceFundConfig', defaultMaintenanceFundConfig));

    // --- TRACKER DE GANHOS INCREMENTAL ---
    const [localTracker, setLocalTracker] = useState(() => storage.getItemSync('rotaDoLucroLocalTracker', {
        amount: 0,
        trips: 0,
        totalAmount: 0,
        totalTrips: 0,
        startDate: null,
        platforms: {}
    }));

    // Configurações
    const [dailyGoal, setDailyGoal] = useState(300); // Sempre número
    const [monthlyWorkDays, setMonthlyWorkDays] = useState(31); // Sempre número
    const [monthlyProfitTarget, setMonthlyProfitTarget] = useState(3000); // Sempre número

    // --- ESTADOS DO SIMULADOR DE METAS (CONQUISTAS) ---
    const [goals, setGoals] = useState(() => storage.getItemSync('mp_goalSimulator_goals', []));
    const goalsRef = useRef(goals);
    const [goalsLastUpdated, setGoalsLastUpdated] = useState(() => storage.getItemSync('mp_goalSimulator_lastUpdated', 0));
    const goalsLastUpdatedRef = useRef(goalsLastUpdated);

    // --- CONTROLE DE TIMESTAMPS DE VEÍCULOS E DESPESAS FIXAS ---
    const [vehiclesLastUpdated, setVehiclesLastUpdated] = useState(() => storage.getItemSync('mp_vehicles_lastUpdated', 0));
    const vehiclesLastUpdatedRef = useRef(vehiclesLastUpdated);

    const [fixedExpensesLastUpdated, setFixedExpensesLastUpdated] = useState(() => storage.getItemSync('mp_fixedExpenses_lastUpdated', 0));
    const fixedExpensesLastUpdatedRef = useRef(fixedExpensesLastUpdated);

    const [pastDueExpensesLastUpdated, setPastDueExpensesLastUpdated] = useState(() => storage.getItemSync('mp_pastDueExpenses_lastUpdated', 0));
    const pastDueExpensesLastUpdatedRef = useRef(pastDueExpensesLastUpdated);

    // --- TOMBSTONES DE ATRASADOS ---
    // IDs de dívidas de meses passados (pastdue_<id>_<YYYY-MM>) que o usuário já
    // pagou ou excluiu. A virada de mês NUNCA regenera esses IDs — sem isso, uma
    // parcela baixada voltava a aparecer a cada reinicialização/sincronização.
    const [pastDueTombstones, setPastDueTombstones] = useState(() => storage.getItemSync('rotaDoLucroPastDueTombstones', []));
    const pastDueTombstonesRef = useRef(pastDueTombstones);

    // --- TOMBSTONES DE VEÍCULOS ---
    // IDs de veículos excluídos. Sem isso, a união por ID (necessária para
    // sincronizar um veículo novo entre aparelhos) ressuscitaria veículos
    // excluídos em outro aparelho.
    const [vehicleTombstones, setVehicleTombstones] = useState(() => storage.getItemSync('rotaDoLucroVehicleTombstones', []));
    const vehicleTombstonesRef = useRef(vehicleTombstones);

    const [pinnedGoalId, setPinnedGoalId] = useState(() => storage.getItemSync('mp_goalSimulator_pinnedGoalId', null));
    const [includePinnedGoalInDailyGoal, setIncludePinnedGoalInDailyGoal] = useState(() => storage.getItemSync('meupainel-include-pinned-goal-in-daily', false));
    const includePinnedGoalInDailyGoalRef = useRef(includePinnedGoalInDailyGoal);
    useEffect(() => { includePinnedGoalInDailyGoalRef.current = includePinnedGoalInDailyGoal; }, [includePinnedGoalInDailyGoal]);
    const [simulationPeriod, setSimulationPeriod] = useState(() => storage.getItemSync('mp_goalSimulator_period', 'endOfYear'));
    const [simulationDailyAmount, setSimulationDailyAmount] = useState(() => storage.getItemSync('mp_goalSimulator_dailyAmount', 0));
    const [simulatorDaysOff, setSimulatorDaysOff] = useState(() => storage.getItemSync('mp_goalSimulator_daysOff', 1));
    const [simulatorRideValue, setSimulatorRideValue] = useState(() => storage.getItemSync('mp_goalSimulator_rideValue', 25));
    const [isCompactViewSettings, setIsCompactViewSettings] = useState(() => storage.getItemSync('mp_goalSimulator_compactView', false));

    // Admin
    const [hideAdminPanel, setHideAdminPanel] = useState(() => storage.getItemSync('meupainel-hide-admin-panel', false));

    // --- NOVAS CONFIGURAÇÕES DE APARÊNCIA ---
    // NOTE: getItemSync returns default on native if cache not yet populated.
    // Values are overwritten asynchronously once isStorageReady is true (see effect below).
    const [accentColor, setAccentColor] = useState(() => storage.getItemSync('meupainel-accent-color', 'indigo'));
    const [compactMode, setCompactMode] = useState(() => storage.getItemSync('meupainel-compact-mode', false));
    const [showCardComparisons, setShowCardComparisons] = useState(() => storage.getItemSync('meupainel-show-card-comparisons', false));
    const [appearanceLoaded, setAppearanceLoaded] = useState(false);

    // Turno ativo
    const [activeShift, setActiveShift] = useState(() => {
        return storage.getItemSync('rotaDoLucroActiveShift', null);
    });
    const [activeShiftKey, setActiveShiftKey] = useState(null);

    // Toast
    const [toast, setToast] = useState({ visible: false, message: '', type: 'info' });

    // --- REFS PARA SINCRONIZAÇÃO CENTRALIZADA (Acessíveis pelo App.js) ---
    const transactionsRef = useRef(transactions);
    const vehiclesRef = useRef(vehicles);
    const userProfileRef = useRef(userProfile);
    const kmRecordsRef = useRef(kmRecords);
    const customCategoriesRef = useRef(customCategories);
    const fixedExpensesRef = useRef(fixedExpenses);
    const scheduledTripsRef = useRef(scheduledTrips);
    const dailyManualStatsRef = useRef(dailyManualStats);
    const dailyGoalRef = useRef(dailyGoal);
    const monthlyWorkDaysRef = useRef(monthlyWorkDays);
    const monthlyProfitTargetRef = useRef(monthlyProfitTarget);
    const manualVarCostRef = useRef(manualVarCost);
    const manualSavedAmountRef = useRef(manualSavedAmount);
    const manualVarCostTypeRef = useRef(manualVarCostType);
    const varCostModeRef = useRef(varCostMode);
    const pastDueExpensesRef = useRef(pastDueExpenses);
    const activeShiftKeyRef = useRef(activeShiftKey);
    const activeShiftRef = useRef(activeShift);
    const themeRef = useRef(theme);
    const hideAdminPanelRef = useRef(hideAdminPanel);
    const lastRolloverMonthRef = useRef(lastRolloverMonth);
    const pinnedGoalIdRef = useRef(pinnedGoalId);
    const simulationPeriodRef = useRef(simulationPeriod);
    const simulationDailyAmountRef = useRef(simulationDailyAmount);
    const simulatorDaysOffRef = useRef(simulatorDaysOff);
    const simulatorRideValueRef = useRef(simulatorRideValue);

    const dynamicGoalsConfigRef = useRef(dynamicGoalsConfig);
    const maintenanceFundConfigRef = useRef(maintenanceFundConfig);

    // Atualiza refs sempre que o estado muda
    useEffect(() => { transactionsRef.current = transactions; }, [transactions]);
    useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);
    useEffect(() => { vehiclesLastUpdatedRef.current = vehiclesLastUpdated; }, [vehiclesLastUpdated]);
    useEffect(() => { goalsLastUpdatedRef.current = goalsLastUpdated; }, [goalsLastUpdated]);
    useEffect(() => { fixedExpensesLastUpdatedRef.current = fixedExpensesLastUpdated; }, [fixedExpensesLastUpdated]);
    useEffect(() => { pastDueExpensesLastUpdatedRef.current = pastDueExpensesLastUpdated; }, [pastDueExpensesLastUpdated]);
    useEffect(() => { pastDueTombstonesRef.current = pastDueTombstones; }, [pastDueTombstones]);
    useEffect(() => { vehicleTombstonesRef.current = vehicleTombstones; }, [vehicleTombstones]);
    useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);
    useEffect(() => { kmRecordsRef.current = kmRecords; }, [kmRecords]);
    useEffect(() => { customCategoriesRef.current = customCategories; }, [customCategories]);
    useEffect(() => { fixedExpensesRef.current = fixedExpenses; }, [fixedExpenses]);
    useEffect(() => { scheduledTripsRef.current = scheduledTrips; }, [scheduledTrips]);
    useEffect(() => { dailyManualStatsRef.current = dailyManualStats; }, [dailyManualStats]);
    useEffect(() => { dailyGoalRef.current = dailyGoal; }, [dailyGoal]);
    useEffect(() => { monthlyWorkDaysRef.current = monthlyWorkDays; }, [monthlyWorkDays]);
    useEffect(() => { monthlyProfitTargetRef.current = monthlyProfitTarget; }, [monthlyProfitTarget]);
    useEffect(() => { manualVarCostRef.current = manualVarCost; }, [manualVarCost]);
    useEffect(() => { manualSavedAmountRef.current = manualSavedAmount; }, [manualSavedAmount]);
    useEffect(() => { manualVarCostTypeRef.current = manualVarCostType; }, [manualVarCostType]);
    useEffect(() => { varCostModeRef.current = varCostMode; }, [varCostMode]);
    useEffect(() => { pastDueExpensesRef.current = pastDueExpenses; }, [pastDueExpenses]);
    useEffect(() => { activeShiftKeyRef.current = activeShiftKey; }, [activeShiftKey]);
    useEffect(() => { activeShiftRef.current = activeShift; }, [activeShift]);
    useEffect(() => { themeRef.current = theme; }, [theme]);
    useEffect(() => { hideAdminPanelRef.current = hideAdminPanel; }, [hideAdminPanel]);
    useEffect(() => { lastRolloverMonthRef.current = lastRolloverMonth; }, [lastRolloverMonth]);
    useEffect(() => { pinnedGoalIdRef.current = pinnedGoalId; }, [pinnedGoalId]);
    useEffect(() => { simulationPeriodRef.current = simulationPeriod; }, [simulationPeriod]);
    useEffect(() => { simulationDailyAmountRef.current = simulationDailyAmount; }, [simulationDailyAmount]);
    useEffect(() => { simulatorDaysOffRef.current = simulatorDaysOff; }, [simulatorDaysOff]);
    useEffect(() => { simulatorRideValueRef.current = simulatorRideValue; }, [simulatorRideValue]);
    useEffect(() => { dynamicGoalsConfigRef.current = dynamicGoalsConfig; }, [dynamicGoalsConfig]);
    useEffect(() => { maintenanceFundConfigRef.current = maintenanceFundConfig; }, [maintenanceFundConfig]);

    // Load appearance settings and other critical config states asynchronously after native cache is ready
    useEffect(() => {
        if (!isStorageReady || appearanceLoaded) return;
        
        Promise.all([
            storage.getItem('mp_vehicles_lastUpdated'),
            storage.getItem('mp_goalSimulator_lastUpdated'),
            storage.getItem('mp_fixedExpenses_lastUpdated'),
            storage.getItem('mp_pastDueExpenses_lastUpdated'),
            storage.getItem('rotaDoLucroPastDueTombstones'),
            storage.getItem('rotaDoLucroVehicleTombstones'),
            storage.getItem('rotaDoLucroDynamicGoalsConfig'),
            storage.getItem('rotaDoLucroMaintenanceFundConfig'),
            storage.getItem('rotaDoLucroLocalTracker'),
            storage.getItem('rotaDoLucroTheme'),
            storage.getItem('rotaDoLucroThemeSchedule'),
            storage.getItem('meupainel-accent-color'),
            storage.getItem('meupainel-compact-mode'),
            storage.getItem('meupainel-show-card-comparisons')
        ]).then(([vehsUpdated, goalsUpdated, fxUpdated, pastDueUpdated, tombstones, vehTombs, dynGoals, maintFund, tracker, savedTheme, savedThemeSchedule, accent, compact, cardComp]) => {
            if (vehsUpdated !== null) {
                setVehiclesLastUpdated(vehsUpdated);
                vehiclesLastUpdatedRef.current = vehsUpdated;
            }
            if (goalsUpdated !== null) {
                setGoalsLastUpdated(goalsUpdated);
                goalsLastUpdatedRef.current = goalsUpdated;
            }
            if (fxUpdated !== null) {
                setFixedExpensesLastUpdated(fxUpdated);
                fixedExpensesLastUpdatedRef.current = fxUpdated;
            }
            if (pastDueUpdated !== null) {
                setPastDueExpensesLastUpdated(pastDueUpdated);
                pastDueExpensesLastUpdatedRef.current = pastDueUpdated;
            }
            if (Array.isArray(tombstones) && tombstones.length > 0) {
                setPastDueTombstones(tombstones.slice(-1000));
                pastDueTombstonesRef.current = tombstones.slice(-1000);
            }
            if (Array.isArray(vehTombs) && vehTombs.length > 0) {
                const cappedV = vehTombs.map(String).slice(-1000);
                setVehicleTombstones(cappedV);
                vehicleTombstonesRef.current = cappedV;
            }
            if (dynGoals !== null) {
                setDynamicGoalsConfig(dynGoals);
                dynamicGoalsConfigRef.current = dynGoals;
            }
            if (maintFund !== null) {
                setMaintenanceFundConfig(maintFund);
                maintenanceFundConfigRef.current = maintFund;
            }
            if (tracker !== null) {
                setLocalTracker(tracker);
            }
            if (savedTheme && ['light', 'dark', 'system', 'schedule'].includes(savedTheme)) {
                setTheme(savedTheme);
            }
            if (savedThemeSchedule && typeof savedThemeSchedule === 'object') {
                setThemeSchedule({
                    darkStart: savedThemeSchedule.darkStart || DEFAULT_THEME_SCHEDULE.darkStart,
                    darkEnd: savedThemeSchedule.darkEnd || DEFAULT_THEME_SCHEDULE.darkEnd
                });
            }
            if (accent !== null) setAccentColor(accent);
            if (compact !== null) setCompactMode(compact);
            if (cardComp !== null) setShowCardComparisons(cardComp);
            
            setAppearanceLoaded(true);
        }).catch(err => {
            console.error('[AppContext] Erro ao carregar configurações assíncronas:', err);
            setAppearanceLoaded(true); // Evita travar a inicialização se houver falha
        });
    }, [isStorageReady, appearanceLoaded]);

    // Persistência das novas configs (só após carregamento concluído para não sobrescrever com defaults)
    // Tema e programação por horário têm efeitos próprios para garantir que a preferência
    // salva (especialmente no Android nativo) não seja substituída pelo valor padrão no boot.
    useEffect(() => {
        if (!appearanceLoaded) return;
        storage.setItem('rotaDoLucroTheme', theme);
    }, [theme, appearanceLoaded]);
    useEffect(() => {
        if (!appearanceLoaded) return;
        storage.setItem('rotaDoLucroThemeSchedule', themeSchedule);
    }, [themeSchedule, appearanceLoaded]);
    useEffect(() => {
        if (!appearanceLoaded) return;
        storage.setItem('meupainel-accent-color', accentColor);
        document.documentElement.setAttribute('data-accent', accentColor);
    }, [accentColor, appearanceLoaded]);
    useEffect(() => {
        if (!appearanceLoaded) return;
        storage.setItem('meupainel-compact-mode', compactMode);
    }, [compactMode, appearanceLoaded]);
    useEffect(() => {
        if (!appearanceLoaded) return;
        storage.setItem('meupainel-show-card-comparisons', showCardComparisons);
    }, [showCardComparisons, appearanceLoaded]);

    // --- FUNÇÕES UTILITÁRIAS MEMOIZADAS ---
    const showToast = useCallback((message, type = 'info') => {
        setToast({ visible: true, message, type });
        setTimeout(() => {
            setToast(prev => ({ ...prev, visible: false }));
        }, 3000);
    }, []);

    const getCategories = useCallback((type) => {
        const defaults = type === 'income' ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
        const customs = customCategories[type];
        const uniqueCustoms = customs.filter(c => !defaults.map(d => d.toLowerCase()).includes(c.toLowerCase()));
        return [...defaults, ...uniqueCustoms];
    }, [customCategories]);

    const formatCurrency = useCallback((val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    }, []);

    const formatKm = useCallback((val) => {
        return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(val);
    }, []);

    const isDarkModeActive = useCallback(() => {
        if (theme === 'dark') return true;
        if (theme === 'light') return false;
        if (theme === 'schedule') return isTimeInDarkSchedule(themeSchedule);
        return systemPrefersDark;
    }, [theme, systemPrefersDark, themeSchedule, timeTick]);

    // Listener para detectar mudanças de tema do sistema operacional em tempo real
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            console.log('APP_CONTEXT: Sistema mudou tema para:', e.matches ? 'dark' : 'light');
            setSystemPrefersDark(e.matches);
        };
        mediaQuery.addEventListener('change', handleChange);
        // Sincroniza o valor inicial para garantir consistência
        setSystemPrefersDark(mediaQuery.matches);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const syncSubscriptionWithServer = useCallback(async () => {
        if (!firebaseUser?.uid) return false;
        try {
            const userDocRef = doc(db, 'user_data', firebaseUser.uid, 'private', 'main');
            const docSnap = await getDoc(userDocRef);
            if (docSnap.exists()) {
                const userData = docSnap.data();
                let sub = userData.subscription || { plan: 'free' };

                const appliedCoupons = userData.appliedCoupons || [];
                let couponTrialExp = null;
                appliedCoupons.forEach(c => {
                    if (c.extraDays > 0) {
                        const appliedTime = c.appliedAt ? new Date(c.appliedAt).getTime() : Date.now();
                        const cExp = appliedTime + (c.extraDays * 24 * 60 * 60 * 1000);
                        if (!couponTrialExp || cExp > couponTrialExp) {
                            couponTrialExp = cExp;
                        }
                    }
                });

                if (couponTrialExp && Date.now() < couponTrialExp && (!sub.plan || sub.plan === 'free' || sub.plan === 'trial' || sub.isTrial)) {
                    sub = {
                        ...sub,
                        plan: 'premium',
                        planId: sub.planId || 'trial_7_days',
                        expiresAt: new Date(couponTrialExp).toISOString(),
                        isTrial: true
                    };
                } else if (!sub.plan || sub.plan === 'free' || sub.plan === 'trial' || sub.isTrial) {
                    const creationTime = new Date(firebaseUser?.metadata?.creationTime || Date.now()).getTime();
                    const baseTrialDays = Number(storage.getItemSync('defaultTrialDays', 7)) || 7;
                    const trialExpiresAt = creationTime + (baseTrialDays * 24 * 60 * 60 * 1000);
                    if (Date.now() < trialExpiresAt) {
                        sub = {
                            ...sub,
                            plan: 'premium',
                            planId: sub.planId || 'trial_days',
                            expiresAt: new Date(trialExpiresAt).toISOString(),
                            isTrial: true
                        };
                    }
                }

                setSubscription(sub);
                storage.setItem('rotaDoLucroSubscription', sub);
                return true;
            }
            return false;
        } catch (error) {
            console.error("Erro syncSubscriptionWithServer:", error);
            return false;
        }
    }, [firebaseUser?.uid]);


    // --- CÁLCULOS MEMOIZADOS ---
    const monthlyData = useMemo(() => {
        const history = {};
        transactions.forEach(t => {
            const monthKey = t.date.substring(0, 7);
            if (!history[monthKey]) history[monthKey] = { income: 0, expense: 0, profit: 0 };
            if (t.type === 'income') history[monthKey].income += t.amount;
            else history[monthKey].expense += t.amount;
        });
        Object.keys(history).forEach(key => {
            history[key].profit = history[key].income - history[key].expense;
        });
        return Object.entries(history).sort((a, b) => b[0].localeCompare(a[0]));
    }, [transactions]);

    const todayTransactions = useMemo(() => {
        const today = getTodayDateKey();
        return transactions.filter(t => t.date.startsWith(today));
    }, [transactions]);

    const todayStats = useMemo(() => {
        const income = todayTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
        const expense = todayTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
        return { income, expense, profit: income - expense };
    }, [todayTransactions]);

    // --- PERSISTÊNCIA CONSOLIDADA COM DEBOUNCE ---
    // Um único efeito substitui os 35 anteriores, reduzindo o custo de inicialização no Android.
    // O debounce de 400ms garante que o storage só é gravado após o estado estabilizar.
    useEffect(() => {
        if (!isLocalDataLoaded) return;
        const timer = setTimeout(() => {
            storage.setItem('rotaDoLucroData', transactions);
            storage.setItem('rotaDoLucroVehicles', vehicles);
            storage.setItem('rotaDoLucroProfile', userProfile);
            storage.setItem('rotaDoLucroKm', kmRecords);
            storage.setItem('rotaDoLucroCustomCategories', customCategories);
            storage.setItem('rotaDoLucroFixedExpenses', fixedExpenses);
            storage.setItem('rotaDoLucroScheduledTrips', scheduledTrips);
            storage.setItem('rotaDoLucroDailyManualStats', dailyManualStats);
            storage.setItem('dailyGoal', dailyGoal);
            storage.setItem('rotaDoLucroWorkDays', monthlyWorkDays);
            storage.setItem('rotaDoLucroProfitTarget', monthlyProfitTarget);
            storage.setItem('deletedDefaultCategories', deletedDefaultCategories);
            storage.setItem('rotaDoLucroSubscription', subscription);
            storage.setItem('rotaDoLucroPastDueExpenses', pastDueExpenses);
            storage.setItem('rotaDoLucroManualVarCost', manualVarCost);
            storage.setItem('rotaDoLucroManualSaved', manualSavedAmount);
            storage.setItem('rotaDoLucroManualVarCostType', manualVarCostType);
            storage.setItem('rotaDoLucroVarCostMode', varCostMode);
            storage.setItem('rotaDoLucroSelectedVehicle', selectedVehicleId);
            storage.setItem('meupainel-last-rollover-month', lastRolloverMonth);
            storage.setItem('mp_goalSimulator_goals', goals);
            storage.setItem('mp_goalSimulator_pinnedGoalId', pinnedGoalId);
            storage.setItem('meupainel-include-pinned-goal-in-daily', includePinnedGoalInDailyGoal);
            storage.setItem('mp_goalSimulator_period', simulationPeriod);
            storage.setItem('mp_goalSimulator_dailyAmount', simulationDailyAmount);
            storage.setItem('mp_goalSimulator_daysOff', simulatorDaysOff);
            storage.setItem('mp_goalSimulator_rideValue', simulatorRideValue);
            storage.setItem('mp_goalSimulator_compactView', isCompactViewSettings);
            storage.setItem('meupainel-hide-admin-panel', hideAdminPanel);
            storage.setItem('rotaDoLucroLocalTracker', localTracker);
            storage.setItem('mp_vehicles_lastUpdated', vehiclesLastUpdated);
            storage.setItem('mp_goalSimulator_lastUpdated', goalsLastUpdated);
            storage.setItem('mp_fixedExpenses_lastUpdated', fixedExpensesLastUpdated);
            storage.setItem('mp_pastDueExpenses_lastUpdated', pastDueExpensesLastUpdated);
            storage.setItem('rotaDoLucroPastDueTombstones', pastDueTombstones);
            storage.setItem('rotaDoLucroDynamicGoalsConfig', dynamicGoalsConfig);
            storage.setItem('rotaDoLucroMaintenanceFundConfig', maintenanceFundConfig);
            if (activeShiftKey) storage.setItem('rotaDoLucroActiveShiftKey', activeShiftKey);
        }, 400);
        return () => clearTimeout(timer);
    }, [
        isLocalDataLoaded, transactions, vehicles, userProfile, kmRecords,
        customCategories, fixedExpenses, scheduledTrips, dailyManualStats,
        dailyGoal, monthlyWorkDays, monthlyProfitTarget, deletedDefaultCategories,
        subscription, pastDueExpenses, manualVarCost, manualSavedAmount,
        manualVarCostType, varCostMode, selectedVehicleId, lastRolloverMonth,
        goals, pinnedGoalId, includePinnedGoalInDailyGoal, simulationPeriod, simulationDailyAmount,
        simulatorDaysOff, simulatorRideValue, isCompactViewSettings,
        hideAdminPanel, localTracker, vehiclesLastUpdated, goalsLastUpdated, activeShiftKey,
        fixedExpensesLastUpdated, pastDueExpensesLastUpdated, pastDueTombstones,
        dynamicGoalsConfig, maintenanceFundConfig
    ]);


    // Atualiza a Ref das Metas
    useEffect(() => {
        goalsRef.current = goals;
    }, [goals]);

    // Custom setGoals que atualiza o timestamp local (Usado localmente pelo usuário)
    const setGoalsWithTimestamp = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(goalsRef.current) : valueOrFn;
        const now = Date.now();
        goalsRef.current = next;
        goalsLastUpdatedRef.current = now;
        setGoals(next);
        setGoalsLastUpdated(now);
        storage.setItem('mp_goalSimulator_lastUpdated', now);
        storage.setItem('mp_goalSimulator_goals', next);
    }, []);

    // Custom setVehicles que atualiza o timestamp local (Usado localmente pelo usuário)
    const setVehiclesWithTimestamp = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(vehiclesRef.current) : valueOrFn;
        const now = Date.now();
        vehiclesRef.current = next;
        vehiclesLastUpdatedRef.current = now;
        setVehicles(next);
        setVehiclesLastUpdated(now);
        storage.setItem('mp_vehicles_lastUpdated', now);
        storage.setItem('rotaDoLucroVehicles', next);
    }, []);

    const setFixedExpensesWithTimestamp = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(fixedExpensesRef.current) : valueOrFn;
        const now = Date.now();
        fixedExpensesRef.current = next;
        fixedExpensesLastUpdatedRef.current = now;
        setFixedExpenses(next);
        setFixedExpensesLastUpdated(now);
        storage.setItem('mp_fixedExpenses_lastUpdated', now);
        storage.setItem('rotaDoLucroFixedExpenses', next);
    }, []);

    const setVehiclesSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(vehiclesRef.current) : valueOrFn;
        vehiclesRef.current = next;
        setVehicles(next);
    }, []);

    const setGoalsSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(goalsRef.current) : valueOrFn;
        goalsRef.current = next;
        setGoals(next);
        storage.setItem('mp_goalSimulator_goals', next);
    }, []);

    // --- SETTERS SINCRONIZADOS PARA PREVENIR RACE CONDITIONS NO SYNC ---
    const setTransactionsSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(transactionsRef.current) : valueOrFn;
        transactionsRef.current = next;
        setTransactions(next);
    }, []);

    const setUserProfileSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(userProfileRef.current) : valueOrFn;
        userProfileRef.current = next;
        setUserProfile(next);
    }, []);

    const setKmRecordsSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(kmRecordsRef.current) : valueOrFn;
        kmRecordsRef.current = next;
        setKmRecords(next);
    }, []);

    const setCustomCategoriesSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(customCategoriesRef.current) : valueOrFn;
        customCategoriesRef.current = next;
        setCustomCategories(next);
    }, []);

    const setFixedExpensesSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(fixedExpensesRef.current) : valueOrFn;
        fixedExpensesRef.current = next;
        setFixedExpenses(next);
        storage.setItem('rotaDoLucroFixedExpenses', next);
    }, []);

    const setScheduledTripsSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(scheduledTripsRef.current) : valueOrFn;
        scheduledTripsRef.current = next;
        setScheduledTrips(next);
    }, []);

    const setDailyManualStatsSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(dailyManualStatsRef.current) : valueOrFn;
        dailyManualStatsRef.current = next;
        setDailyManualStats(next);
    }, []);

    const setPastDueExpensesSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(pastDueExpensesRef.current) : valueOrFn;
        pastDueExpensesRef.current = next;
        setPastDueExpenses(next);
        storage.setItem('rotaDoLucroPastDueExpenses', next);
    }, []);

    // Setter com timestamp: qualquer baixa/remoção/adição de atrasados atualiza
    // o relógio lógico, dispara o auto-sync e permite merge correto entre aparelhos.
    const setPastDueExpensesWithTimestamp = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(pastDueExpensesRef.current) : valueOrFn;
        const now = Date.now();
        pastDueExpensesRef.current = next;
        pastDueExpensesLastUpdatedRef.current = now;
        setPastDueExpenses(next);
        setPastDueExpensesLastUpdated(now);
        storage.setItem('mp_pastDueExpenses_lastUpdated', now);
        storage.setItem('rotaDoLucroPastDueExpenses', next);
    }, []);

    // Remove atrasados REGISTRANDO tombstone: os IDs removidos nunca mais são
    // regenerados pela virada de mês, em nenhum aparelho (tombstones sincronizam).
    const removePastDueWithTombstone = useCallback((ids) => {
        const idsSet = new Set(Array.isArray(ids) ? ids : [ids]);
        const next = (pastDueExpensesRef.current || []).filter(e => !idsSet.has(e.id));
        const now = Date.now();
        pastDueExpensesRef.current = next;
        pastDueExpensesLastUpdatedRef.current = now;
        setPastDueExpenses(next);
        setPastDueExpensesLastUpdated(now);
        storage.setItem('mp_pastDueExpenses_lastUpdated', now);
        storage.setItem('rotaDoLucroPastDueExpenses', next);
        // Une os novos tombstones aos existentes (limite de 1000 para não crescer sem fim)
        const merged = [...(pastDueTombstonesRef.current || [])];
        idsSet.forEach(id => { if (typeof id === 'string' && id.startsWith('pastdue_') && !merged.includes(id)) merged.push(id); });
        const capped = merged.slice(-1000);
        pastDueTombstonesRef.current = capped;
        setPastDueTombstones(capped);
        storage.setItem('rotaDoLucroPastDueTombstones', capped);
        return next;
    }, []);

    // Remove veículo REGISTRANDO tombstone: o ID excluído nunca mais é
    // ressuscitado pela união por ID em outro aparelho.
    const removeVehicleWithTombstone = useCallback((ids) => {
        const idsArr = Array.isArray(ids) ? ids : [ids];
        const idsStr = idsArr.map(v => String(v));
        const idsSet = new Set(idsStr);
        const next = (vehiclesRef.current || []).filter(v => !idsSet.has(String(v.id)));
        const now = Date.now();
        vehiclesRef.current = next;
        vehiclesLastUpdatedRef.current = now;
        setVehicles(next);
        setVehiclesLastUpdated(now);
        storage.setItem('mp_vehicles_lastUpdated', now);
        storage.setItem('rotaDoLucroVehicles', next);
        const merged = [...(vehicleTombstonesRef.current || []).map(String)];
        idsStr.forEach(id => { if (!merged.includes(id)) merged.push(id); });
        const cappedV = merged.slice(-1000);
        vehicleTombstonesRef.current = cappedV;
        setVehicleTombstones(cappedV);
        storage.setItem('rotaDoLucroVehicleTombstones', cappedV);
        return next;
    }, []);

    const setActiveShiftSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(activeShiftRef.current) : valueOrFn;
        activeShiftRef.current = next;
        setActiveShift(next);
    }, []);

    const setActiveShiftKeySync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(activeShiftKeyRef.current) : valueOrFn;
        activeShiftKeyRef.current = next;
        setActiveShiftKey(next);
    }, []);

    const setDailyGoalSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(dailyGoalRef.current) : valueOrFn;
        dailyGoalRef.current = next;
        setDailyGoal(next);
    }, []);

    const setMonthlyWorkDaysSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(monthlyWorkDaysRef.current) : valueOrFn;
        monthlyWorkDaysRef.current = next;
        setMonthlyWorkDays(next);
    }, []);

    const setMonthlyProfitTargetSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(monthlyProfitTargetRef.current) : valueOrFn;
        monthlyProfitTargetRef.current = next;
        setMonthlyProfitTarget(next);
    }, []);

    const setDynamicGoalsConfigSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(dynamicGoalsConfigRef.current) : valueOrFn;
        dynamicGoalsConfigRef.current = next;
        setDynamicGoalsConfig(next);
    }, []);

    const setMaintenanceFundConfigSync = useCallback((valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(maintenanceFundConfigRef.current) : valueOrFn;
        maintenanceFundConfigRef.current = next;
        setMaintenanceFundConfig(next);
    }, []);

    // 3. OUVINTE DE ESTADO DO CELULAR (Mitigar Perda de Dados)
    const stateRef = useRef(null);
    useEffect(() => {
        stateRef.current = {
            transactions, vehicles, userProfile, theme, systemPrefersDark, kmRecords,
            customCategories, fixedExpenses, scheduledTrips, dailyManualStats,
            dailyGoal, monthlyWorkDays, monthlyProfitTarget, activeShiftKey,
            firebaseUser, isInitialLoadDone,
            pastDueExpenses, manualVarCost, manualSavedAmount, manualVarCostType, varCostMode,
            goals, pinnedGoalId, simulationPeriod, simulationDailyAmount, simulatorDaysOff, simulatorRideValue,
            localTracker, lastRolloverMonth,
            accentColor, compactMode, showCardComparisons, // BUG FIX: include appearance settings in background save
            goalsLastUpdated, vehiclesLastUpdated, fixedExpensesLastUpdated,
            pastDueExpensesLastUpdated, pastDueTombstones, vehicleTombstones,
            dynamicGoalsConfig, maintenanceFundConfig
        };
    }, [transactions, vehicles, userProfile, theme, systemPrefersDark, kmRecords, customCategories,
        fixedExpenses, scheduledTrips, dailyManualStats, dailyGoal, monthlyWorkDays,
        monthlyProfitTarget, activeShiftKey, firebaseUser, isInitialLoadDone,
        pastDueExpenses, manualVarCost, manualSavedAmount, manualVarCostType, varCostMode,
        goals, pinnedGoalId, simulationPeriod, simulationDailyAmount, simulatorDaysOff, simulatorRideValue,
        isCompactViewSettings, localTracker, accentColor, compactMode, showCardComparisons, goalsLastUpdated, vehiclesLastUpdated, fixedExpensesLastUpdated, pastDueExpensesLastUpdated, pastDueTombstones, vehicleTombstones, dynamicGoalsConfig, maintenanceFundConfig]);

    useEffect(() => {
        let listener = null;
        const setupCapacitor = async () => {
            try {
                listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                    // SE O APP FUGIR DA TELA (BACKGROUND), FORÇA SALVAR NA HORA SEM DEBOUNCE
                    if (!isActive && stateRef.current) {
                        const s = stateRef.current;
                        // Salvamento COMPLETO no storage local (todos os campos)
                        storage.setItem('rotaDoLucroData', s.transactions);
                        storage.setItem('rotaDoLucroVehicles', s.vehicles);
                        storage.setItem('rotaDoLucroProfile', s.userProfile);
                        storage.setItem('rotaDoLucroTheme', s.theme);
                        storage.setItem('rotaDoLucroKm', s.kmRecords);
                        storage.setItem('rotaDoLucroCustomCategories', s.customCategories);
                        storage.setItem('rotaDoLucroFixedExpenses', s.fixedExpenses);
                        storage.setItem('rotaDoLucroScheduledTrips', s.scheduledTrips);
                        storage.setItem('rotaDoLucroDailyManualStats', s.dailyManualStats);
                        storage.setItem('rotaDoLucroGoal', s.dailyGoal);
                        storage.setItem('dailyGoal', s.dailyGoal);
                        storage.setItem('rotaDoLucroWorkDays', s.monthlyWorkDays);
                        storage.setItem('rotaDoLucroProfitTarget', s.monthlyProfitTarget);
                        storage.setItem('rotaDoLucroPastDueExpenses', s.pastDueExpenses);
                        storage.setItem('rotaDoLucroManualVarCost', s.manualVarCost);
                        storage.setItem('rotaDoLucroManualSaved', s.manualSavedAmount);
                        storage.setItem('rotaDoLucroManualVarCostType', s.manualVarCostType);
                        storage.setItem('rotaDoLucroVarCostMode', s.varCostMode);
                        storage.setItem('mp_goalSimulator_goals', s.goals);
                        storage.setItem('mp_goalSimulator_pinnedGoalId', s.pinnedGoalId);
                        storage.setItem('mp_goalSimulator_period', s.simulationPeriod);
                        storage.setItem('mp_goalSimulator_dailyAmount', s.simulationDailyAmount);
                        storage.setItem('mp_goalSimulator_daysOff', s.simulatorDaysOff);
                        storage.setItem('mp_goalSimulator_rideValue', s.simulatorRideValue);
                        storage.setItem('mp_goalSimulator_compactView', s.isCompactViewSettings);
                        storage.setItem('rotaDoLucroLocalTracker', s.localTracker);
                        storage.setItem('meupainel-last-rollover-month', s.lastRolloverMonth);
                        // BUG FIX: save appearance settings on background
                        storage.setItem('meupainel-accent-color', s.accentColor);
                        storage.setItem('meupainel-compact-mode', s.compactMode);
                        storage.setItem('meupainel-show-card-comparisons', s.showCardComparisons);
                        storage.setItem('rotaDoLucroDynamicGoalsConfig', s.dynamicGoalsConfig);
                        storage.setItem('rotaDoLucroMaintenanceFundConfig', s.maintenanceFundConfig);
                        storage.setItem('mp_fixedExpenses_lastUpdated', s.fixedExpensesLastUpdated);
                        storage.setItem('mp_pastDueExpenses_lastUpdated', s.pastDueExpensesLastUpdated);
                        storage.setItem('rotaDoLucroPastDueTombstones', s.pastDueTombstones);
                        storage.setItem('rotaDoLucroVehicleTombstones', s.vehicleTombstones);
                        storage.setItem('mp_vehicles_lastUpdated', s.vehiclesLastUpdated);
                        storage.setItem('mp_goalSimulator_lastUpdated', s.goalsLastUpdated);
                        if (s.activeShiftKey) storage.setItem('rotaDoLucroActiveShiftKey', s.activeShiftKey);

                        // Código movido para App.js e serviços dedicados para evitar sync loop e duplicação
                    }
                });
            } catch (err) { }
        };
        setupCapacitor();
        return () => { if (listener && listener.remove) listener.remove(); };
    }, []);

    // 4. OUVINTE EM TEMPO REAL DA ASSINATURA (Crucial para Webhooks)
    useEffect(() => {
        if (!firebaseUser?.uid) return;

        console.log('APP_CONTEXT: Iniciando listener de assinatura para:', firebaseUser.uid);
        const userDocRef = doc(db, 'user_data', firebaseUser.uid, 'private', 'main');

        const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                let sub = data.subscription || {};

                // Suporte reativo a cupons com dias extras
                let couponTrialExp = null;
                if (!sub.plan || sub.plan === 'free' || sub.plan === 'trial' || sub.isTrial) {
                    const appliedCoupons = data.appliedCoupons || [];
                    appliedCoupons.forEach(c => {
                        if (c.extraDays > 0) {
                            const appliedTime = c.appliedAt ? new Date(c.appliedAt).getTime() : Date.now();
                            const cExp = appliedTime + (c.extraDays * 24 * 60 * 60 * 1000);
                            if (!couponTrialExp || cExp > couponTrialExp) {
                                couponTrialExp = cExp;
                            }
                        }
                    });
                    if (couponTrialExp && Date.now() < couponTrialExp) {
                        sub = {
                            ...sub,
                            plan: 'premium',
                            planId: sub.planId || 'trial_7_days',
                            expiresAt: new Date(couponTrialExp).toISOString(),
                            isTrial: true
                        };
                    }
                }

                // Se houver plano pago direto na nuvem ou cupom com dias extras, atualiza.
                // Se não houver, deixa a cargo do App.js (que calcula o trial com appSettings dinâmico).
                const hasExplicitPaidPlan = sub && (sub.plan === 'premium' || sub.plan === 'vip') && !sub.isTrial;
                const hasCouponTrial = couponTrialExp && Date.now() < couponTrialExp;

                if (hasExplicitPaidPlan || hasCouponTrial) {
                    console.log('APP_CONTEXT: Nova assinatura detectada na nuvem:', sub.plan);
                    setSubscription(sub);
                }
            }
            setIsSubscriptionLoaded(true); // Sempre marca como carregado, mesmo se doc não existe
        }, (error) => {
            console.error('Erro no listener de assinatura:', error);
            setIsSubscriptionLoaded(true); // Fallback para não travar o app
        });

        return () => unsubscribe();
    }, [firebaseUser?.uid]);

    // --- RESET AUTOMÁTICO DE APARÊNCIA E EXPIRAÇÃO OFFLINE ---
    useEffect(() => {
        // Só executa se a assinatura já foi carregada e não é Admin
        if (!isSubscriptionLoaded || !firebaseUser || isAdmin(firebaseUser?.uid)) return;

        // Verifica se é Premium ou VIP ativo
        const end = subscription?.expiresAt
            ? (subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : new Date(subscription.expiresAt))
            : new Date(0);
        const isNotExpired = !isNaN(end.getTime()) && end.getTime() > Date.now();
        const isPartner = userProfile?.isPartner === true;
        const isTrial = subscription?.isTrial === true;
        const isPremium = subscription?.plan === 'vip' || (subscription?.plan === 'premium' && isNotExpired) || isPartner || isTrial;

        // Se expirou offline (plano no cache era premium/trial mas agora é inválido/passado)
        if (!isPremium && subscription?.plan && subscription.plan !== 'free') {
            console.log('APP_CONTEXT: Assinatura expirada offline. Atualizando estado e cache local para gratuito.');
            const expiredSub = { plan: 'free', expiresAt: null };
            setSubscription(expiredSub);
            storage.setItem('rotaDoLucroSubscription', expiredSub);
            return;
        }

        // Se NÃO for premium, força os padrões de volta
        if (!isPremium) {
            if (accentColor !== 'indigo') {
                console.log('APP_CONTEXT: Assinatura expirada. Resetando Cor de Destaque para padrão.');
                setAccentColor('indigo');
            }
            if (compactMode !== false) {
                console.log('APP_CONTEXT: Assinatura expirada. Resetando Modo Compacto para padrão.');
                setCompactMode(false);
            }
        }
    }, [subscription, firebaseUser, isSubscriptionLoaded, accentColor, compactMode, userProfile?.isPartner]);

    // --- EXPOSIÇÃO DO CONTEXTO MEMOIZADA ---
    const value = useMemo(() => ({
        transactions, setTransactions: setTransactionsSync,
        vehicles, setVehicles: setVehiclesWithTimestamp,
        setVehiclesDirectly: setVehiclesSync,
        vehiclesLastUpdated, setVehiclesLastUpdated,
        vehiclesLastUpdatedRef,
        userProfile, setUserProfile: setUserProfileSync,
        goals, setGoals: setGoalsWithTimestamp,
        setGoalsDirectly: setGoalsSync,
        goalsRef,
        goalsLastUpdated, setGoalsLastUpdated,
        goalsLastUpdatedRef,
        pinnedGoalId, setPinnedGoalId,
        includePinnedGoalInDailyGoal, setIncludePinnedGoalInDailyGoal,
        simulationPeriod, setSimulationPeriod,
        simulationDailyAmount, setSimulationDailyAmount,
        simulatorDaysOff, setSimulatorDaysOff,
        simulatorRideValue, setSimulatorRideValue,
        isCompactViewSettings, setIsCompactViewSettings,
        theme, setTheme,
        themeSchedule, updateThemeSchedule, setThemeSchedule: updateThemeSchedule,
        systemPrefersDark, setSystemPrefersDark,
        kmRecords, setKmRecords: setKmRecordsSync,
        customCategories, setCustomCategories: setCustomCategoriesSync,
        fixedExpenses, setFixedExpenses: setFixedExpensesWithTimestamp,
        setFixedExpensesDirectly: setFixedExpensesSync,
        fixedExpensesLastUpdated, setFixedExpensesLastUpdated,
        fixedExpensesLastUpdatedRef,
        scheduledTrips, setScheduledTrips: setScheduledTripsSync,
        dailyManualStats, setDailyManualStats: setDailyManualStatsSync,
        deletedDefaultCategories, setDeletedDefaultCategories,
        subscription, setSubscription,
        isSubscriptionLoaded, setIsSubscriptionLoaded,
        syncSubscriptionWithServer,
        isInitialLoadDone, setIsInitialLoadDone,
        isStorageReady, setIsStorageReady,
        isLocalDataLoaded, setIsLocalDataLoaded,
        lastRemoteSync, setLastRemoteSync,
        isSyncing, setIsSyncing,
        lastResetMonth, setLastResetMonth,
        lastRolloverMonth, setLastRolloverMonth,
        pastDueExpenses, setPastDueExpenses: setPastDueExpensesWithTimestamp,
        setPastDueExpensesDirectly: setPastDueExpensesSync,
        pastDueExpensesLastUpdated, setPastDueExpensesLastUpdated,
        pastDueExpensesLastUpdatedRef,
        pastDueTombstones, setPastDueTombstones,
        pastDueTombstonesRef, removePastDueWithTombstone,
        vehicleTombstones, setVehicleTombstones,
        vehicleTombstonesRef, removeVehicleWithTombstone,
        manualVarCost, setManualVarCost,
        manualSavedAmount, setManualSavedAmount,
        manualVarCostType, setManualVarCostType,
        varCostMode, setVarCostMode,
        selectedVehicleId, setSelectedVehicleId,
        dailyGoal, setDailyGoal: setDailyGoalSync,
        monthlyWorkDays, setMonthlyWorkDays: setMonthlyWorkDaysSync,
        monthlyProfitTarget, setMonthlyProfitTarget: setMonthlyProfitTargetSync,
        dynamicGoalsConfig, setDynamicGoalsConfig: setDynamicGoalsConfigSync,
        maintenanceFundConfig, setMaintenanceFundConfig: setMaintenanceFundConfigSync,
        activeShift, setActiveShift: setActiveShiftSync,
        activeShiftKey, setActiveShiftKey: setActiveShiftKeySync,
        hideAdminPanel, setHideAdminPanel,
        localTracker, setLocalTracker,
        accentColor, setAccentColor,
        compactMode, setCompactMode,
        showCardComparisons, setShowCardComparisons,
        toast, showToast,
        getCategories, formatCurrency, formatKm, isDarkModeActive,
        customAppLogo, updateCustomLogo,
        firebaseUser,

        // Refs para o App.js
        transactionsRef, vehiclesRef, userProfileRef, kmRecordsRef,
        customCategoriesRef, fixedExpensesRef, scheduledTripsRef,
        dailyManualStatsRef, dailyGoalRef, monthlyWorkDaysRef,
        monthlyProfitTargetRef, manualVarCostRef, manualSavedAmountRef,
        manualVarCostTypeRef, varCostModeRef, pastDueExpensesRef,
        activeShiftKeyRef, activeShiftRef, themeRef, hideAdminPanelRef,
        lastRolloverMonthRef,
        pinnedGoalIdRef, simulationPeriodRef, simulationDailyAmountRef,
        simulatorDaysOffRef, simulatorRideValueRef,
        vehiclesLastUpdatedRef, dynamicGoalsConfigRef, maintenanceFundConfigRef
    }), [
        transactions, setTransactionsSync,
        vehicles, setVehiclesWithTimestamp, setVehiclesSync, vehiclesLastUpdated,
        userProfile, setUserProfileSync,
        goals, setGoalsWithTimestamp, setGoalsSync, goalsLastUpdated,
        pinnedGoalId, setPinnedGoalId,
        includePinnedGoalInDailyGoal, setIncludePinnedGoalInDailyGoal,
        simulationPeriod, setSimulationPeriod,
        simulationDailyAmount, setSimulationDailyAmount,
        simulatorDaysOff, setSimulatorDaysOff,
        simulatorRideValue, setSimulatorRideValue,
        isCompactViewSettings, setIsCompactViewSettings,
        theme, setTheme,
        themeSchedule, updateThemeSchedule,
        systemPrefersDark, setSystemPrefersDark,
        kmRecords, setKmRecordsSync,
        customCategories, setCustomCategoriesSync,
        fixedExpenses, setFixedExpensesWithTimestamp, setFixedExpensesSync, fixedExpensesLastUpdated,
        scheduledTrips, setScheduledTripsSync,
        dailyManualStats, setDailyManualStatsSync,
        deletedDefaultCategories, setDeletedDefaultCategories,
        subscription, setSubscription,
        isSubscriptionLoaded, setIsSubscriptionLoaded,
        syncSubscriptionWithServer,
        isInitialLoadDone, setIsInitialLoadDone,
        isStorageReady, setIsStorageReady,
        isLocalDataLoaded, setIsLocalDataLoaded,
        lastRemoteSync, setLastRemoteSync,
        isSyncing, setIsSyncing,
        lastResetMonth, setLastResetMonth,
        lastRolloverMonth, setLastRolloverMonth,
        pastDueExpenses, setPastDueExpensesWithTimestamp, setPastDueExpensesSync, pastDueExpensesLastUpdated,
        pastDueTombstones, setPastDueTombstones, removePastDueWithTombstone,
        vehicleTombstones, setVehicleTombstones, removeVehicleWithTombstone,
        manualVarCost, setManualVarCost,
        manualSavedAmount, setManualSavedAmount,
        manualVarCostType, setManualVarCostType,
        varCostMode, setVarCostMode,
        selectedVehicleId, setSelectedVehicleId,
        dailyGoal, setDailyGoalSync,
        monthlyWorkDays, setMonthlyWorkDaysSync,
        monthlyProfitTarget, setMonthlyProfitTargetSync,
        dynamicGoalsConfig, setDynamicGoalsConfigSync,
        maintenanceFundConfig, setMaintenanceFundConfigSync,
        activeShift, setActiveShiftSync,
        activeShiftKey, setActiveShiftKeySync,
        hideAdminPanel, setHideAdminPanel,
        localTracker, setLocalTracker,
        accentColor, setAccentColor,
        compactMode, setCompactMode,
        showCardComparisons, setShowCardComparisons,
        toast, showToast,
        getCategories, formatCurrency, formatKm, isDarkModeActive,
        customAppLogo, updateCustomLogo,
        firebaseUser
    ]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
}

// Hook personalizado para usar o AppContext
export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp deve ser usado dentro de um AppProvider');
    }
    return context;
}
